import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Pencil, Check, X, User } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Inline-editable Name field for the customer Profile page.
 *
 * Pencil click → text turns into an input with Save / Cancel buttons.
 * Save → PATCH /api/auth/me {name}. Updates AuthContext on success.
 * No OTP required (name changes are low-risk and don't affect identity).
 */
export default function EditNameField() {
  const { user, updateUser } = useAuth();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(user?.name || '');
  const [saving, setSaving] = useState(false);

  const startEdit = () => {
    setDraft(user?.name || '');
    setEditing(true);
  };

  const cancel = () => {
    setDraft(user?.name || '');
    setEditing(false);
  };

  const save = async () => {
    const trimmed = (draft || '').trim();
    if (!trimmed) {
      toast.error('Name cannot be empty');
      return;
    }
    if (trimmed === user?.name) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      const { data } = await axios.patch(
        `${API}/api/auth/me`,
        { name: trimmed },
        { withCredentials: true },
      );
      updateUser(data);
      toast.success('Name updated');
      setEditing(false);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Could not update name';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <p className="text-xs uppercase font-bold text-muted-foreground mb-1">Name</p>
      {!editing ? (
        <div className="flex items-center justify-between gap-3" data-testid="profile-name-row">
          <div className="flex items-center gap-3 min-w-0">
            <User className="w-5 h-5 text-muted-foreground flex-shrink-0" />
            <p className="font-bold truncate" data-testid="profile-name-value">{user?.name}</p>
          </div>
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={startEdit}
            data-testid="profile-name-edit-btn"
            aria-label="Edit name"
            className="h-9 w-9 flex-shrink-0"
          >
            <Pencil className="w-4 h-4" />
          </Button>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <Input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Your name"
            maxLength={60}
            autoFocus
            disabled={saving}
            data-testid="profile-name-input"
            className="h-11 rounded-xl"
          />
          <Button
            type="button"
            size="icon"
            onClick={save}
            disabled={saving}
            data-testid="profile-name-save-btn"
            aria-label="Save name"
            className="h-11 w-11 flex-shrink-0 btn-primary"
          >
            <Check className="w-4 h-4" />
          </Button>
          <Button
            type="button"
            size="icon"
            variant="outline"
            onClick={cancel}
            disabled={saving}
            data-testid="profile-name-cancel-btn"
            aria-label="Cancel"
            className="h-11 w-11 flex-shrink-0"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
